'use client';

import { FC } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { AssetUtils, getRoute } from '@ravago/shared/next-data-access';
import { MaxImageSizes } from '@ravago/shared/page-data/enums/max-image-sizes';
import { NodeTypes } from '@ravago/shared/page-data/enums/node-types';
import { ProductCard as ProductCardModel } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { ProductCard as ProductCardRadiance } from '@ravago/shared/radiance/components/card/components/ProductCard/ProductCard';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { useTranslations } from '@ravago/shared/translations/client';

import { useProductCompareSelection } from '../../../../hooks/use-product-compare-selection';
import { AppUtils } from '../../../../utils/app/app.utils';

import { ProductCardBadge } from './components/product-card-badge';

interface Props {
   product: ProductCardModel;
   locale: string;
   isConsumer: boolean;
   hasPublicLabelAndSigningsPermission?: boolean;
   hasSustainabilityLabels?: boolean;
   hasProductCompareFeatureToggle?: boolean;
   acceptWebp?: boolean;
   assetBasePath: string;
   baseRoute?: string;
   useStandardCardBanner?: boolean;
   deferImage?: boolean;
   nodeType?: NodeTypes;
   testId?: string;
}

export const ProductCard: FC<Props> = ({
   product,
   locale,
   isConsumer,
   hasPublicLabelAndSigningsPermission = false,
   hasSustainabilityLabels = false,
   hasProductCompareFeatureToggle = false,
   acceptWebp,
   assetBasePath,
   baseRoute,
   useStandardCardBanner,
   deferImage = false,
   nodeType,
   testId
}) => {
   const translate = useTranslations();
   const { user } = useAuthentication();

   const { isChecked, isDisabled, addProduct, removeProduct } = useProductCompareSelection(product.id);

   const handleProductCompare = () => {
      if (isChecked) {
         removeProduct(product.id);
      } else {
         addProduct(product.id, product.name);
      }
   };

   const image = product.image
      ? AssetUtils.optimizeImageIfRequired(product.image, acceptWebp, MaxImageSizes.SMALL_IMAGE, MaxImageSizes.SMALL_IMAGE)
      : '';

   const card = (
      <ProductCardRadiance
         href={getRoute(locale, baseRoute, product.slug, undefined)}
         target="_self"
         noFollow={product.noFollow}
         image={useStandardCardBanner ? `${assetBasePath}/images/placeholders/default-banner-small.${acceptWebp ? 'webp' : 'png'}` : image}
         name={product.name}
         producer={product.producer ?? ''}
         polymerType={product.polymerSubgroup ?? ''}
         technologies={product.technologies}
         features={product.features}
         fillers={product.fillers}
         badgLeft={
            product.isPreferred &&
            hasPublicLabelAndSigningsPermission &&
            (user?.groups?.includes(UserGroups.ravagoSalesRepresentative) || !isConsumer) ? (
               <Badge variant="info">
                  <Icon icon="gem" />
               </Badge>
            ) : undefined
         }
         badgeRight={
            hasPublicLabelAndSigningsPermission && <ProductCardBadge badgeInfo={product} hasSustainabilityLabels={hasSustainabilityLabels} />
         }
         compareLabel={translate('product.compare-product')}
         toggleState={isChecked}
         fillHeight
         compareDisabled={isDisabled}
         deferImage={deferImage}
         attributes={{
            'test-selector': AppUtils.getTestSelector(`product-${product.name}`, nodeType ?? NodeTypes.PRODUCT_CARD, testId)
         }}
         onToggleCompare={hasProductCompareFeatureToggle ? handleProductCompare : undefined}
      />
   );

   return !isConsumer ? <span style={{ pointerEvents: 'none' }}>{card}</span> : card;
};
